'use client';

import { Box, Container, Stack, styled, Theme, Typography } from '@mui/material';
import Image, { ImageProps } from 'next/image';
import HostDefault from './assets/becomeahost-default.png';
import HostHover from './assets/becomeahost-hover.png';
import CoachDefault from './assets/bookacoach-default.png';
import CoachHover from './assets/bookacoach-hover.png';
import JoinDefault from './assets/joinexperience-default.png';
import JoinHover from './assets/joinexperience-hover.png';
import { ArrowForward, ArrowRightAltRounded, ArrowRightRounded } from '@mui/icons-material';
import Link from 'next/link';
import { config } from '@/config/environment';

const liveItems = [
  {
    title: 'Join an experience',
    description:
      'Getting involved doesn’t have to mean contributing something specific - joining experiences and connecting with others is enough!',
    image: JoinDefault,
    imageHover: JoinHover,
    href: '/explore',
  },
  {
    title: 'Become a host',
    description:
      'Have a skill you’d like to share? Or maybe you have an idea for an group activity you think might be fun? Try hosting your own experience!',
    image: HostDefault,
    imageHover: HostHover,
    href: '/discover/hosting',
  },
  {
    title: 'Book a coach',
    description:
      'Want to improve your ability to navigate life? A coach can help you discover the tools, skills, and strategies you need to succeed!',
    image: CoachDefault,
    imageHover: CoachHover,
    href: '/discover/coaching',
  },
];

const archiveItems = [
  {
    title: 'See past experiences',
    description:
      'Discover how community-led experiences brought people together to spark curiosity, build confidence, and foster lasting connections.',
    image: JoinDefault,
    imageHover: JoinHover,
    href: '/explore',
  },
  {
    title: 'How hosting worked',
    description:
      'See how community members shared their interests, skills, and passions by creating experiences for others.',
    image: HostDefault,
    imageHover: HostHover,
    href: '/discover/hosting',
  },
  {
    title: 'How coaching worked',
    description:
      'Learn how Curiko coaches took a unique approach to busting barriers and enabling community members to pursue their values and desires.',
    image: CoachDefault,
    imageHover: CoachHover,
    href: '/discover/coaching',
  },
];

interface CardProps {
  title: string;
  description: string;
  image: ImageProps['src'];
  imageHover: ImageProps['src'];
  href: string;
}

const StandardImage = styled(Image)({
  position: 'absolute',
  width: '100%',
  height: 'auto',
  aspectRatio: '4/3',
  objectFit: 'contain',
  transition: 'opacity 0.5s ease-in-out',
});

const HoverImage = styled(StandardImage)({
  opacity: 0,
});

const Title = styled(Typography)`
  font-size: 28px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing(1).toString()}px;
  text-decoration: underline;
  text-decoration-thickness: 4px;
  text-decoration-color: transparent;
  transition: all 0.5s ease-in-out;
  text-underline-offset: 10px;
  margin-bottom: 20px;
`;

const CardStack = styled(Stack)`
  cursor: pointer !important;

  &:hover {
    .standard-image {
      opacity: 0;
    }

    .hover-image {
      opacity: 1;
    }

    .title {
      color: ${({ theme }) => theme.palette.common.yellow};
      text-decoration-color: ${({ theme }) => theme.palette.common.yellow};
    }
  }
`;

function Card({ title, description, image, imageHover, href }: CardProps) {
  return (
    <Link href={href} style={{ textDecoration: 'none' }}>
      <CardStack>
        <Box
          position="relative"
          sx={{ width: '100%', height: 'auto', aspectRatio: '4/3', marginBottom: '20px' }}
        >
          <StandardImage className="standard-image" src={image} alt={title} style={{}} />
          <HoverImage className="hover-image" src={imageHover} alt={title} />
        </Box>
        <Title className="title" variant="h3" color="white" fontSize="28px" fontWeight={600}>
          {title} <ArrowForward sx={{ fontSize: '30px !important', marginBottom: '-5px' }} />
        </Title>
        <Typography variant="body1" color="white">
          {description}
        </Typography>
      </CardStack>
    </Link>
  );
}

export function GetInvolved() {
  const archiveMode = config.platformMode === 'archive';
  const items = archiveMode ? archiveItems : liveItems;
  return (
    <Container>
      <Stack
        spacing={4}
        alignItems="center"
        sx={{
          mb: {
            xs: 0,
            sm: 16,
          },
        }}
      >
        <Typography variant="h1" color="white" textAlign="center">
          {archiveMode ? 'Explore Curiko' : 'Get Involved'}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: {
              xs: 'center',
              sm: 'flex-start',
            },
            justifyContent: 'center',
            gap: theme => theme.spacing(4),
          }}
        >
          {items.map((item, index) => (
            <Box key={index} sx={{ width: { xs: '100%', sm: '30%' } }}>
              <Card {...item} />
            </Box>
          ))}
        </Box>
      </Stack>
    </Container>
  );
}
